import React, { useState, useEffect, useRef } from "react";
import { TailSpin } from "react-loader-spinner";
import usCities from "../data/us_cities.json";

// feb2 data URL (raw GitHub)
const FEB2_DATA_URL = "https://raw.githubusercontent.com/ericpgreen/feb2/main/inst/json";

// Historical comparison data (will be loaded from file when available)
let historicalComparison = null;
try {
  historicalComparison = require("../data/historical_comparison.json");
} catch (e) {
  // File not yet generated
}

// Prognosticator descriptions (will be loaded from file when available)
let prognosticatorDescriptions = null;
try {
  prognosticatorDescriptions = require("../data/prognosticator_descriptions.json");
} catch (e) {
  // File not yet generated
}

const Home = () => {
  const [city, setCity] = useState("");
  const [prediction, setPrediction] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [prognosticators, setPrognosticators] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [nearestPrognosticator, setNearestPrognosticator] = useState(null);
  const [probability, setProbability] = useState(null);
  const [pastPrediction, setPastPrediction] = useState(null);
  const [prognosticatorPastPrediction, setPrognosticatorPastPrediction] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [comparisonData, setComparisonData] = useState(null);
  const [isSearchingCities, setIsSearchingCities] = useState(false);
  const inputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const weatherApiKey = process.env.REACT_APP_WEATHER_API_KEY;

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Load prognosticators and predictions from feb2 on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [progResponse, predResponse] = await Promise.all([
          fetch(`${FEB2_DATA_URL}/prognosticators.json`),
          fetch(`${FEB2_DATA_URL}/predictions.json`)
        ]);
        if (progResponse.ok) {
          const data = await progResponse.json();
          setPrognosticators(data.filter(p => p.prognosticator_lat && p.prognosticator_long));
        }
        if (predResponse.ok) {
          const data = await predResponse.json();
          setPredictions(data);
        }
      } catch (err) {
        console.log("Could not load feb2 data:", err);
      }
    };
    loadData();
  }, []);

  // Calculate distance between two coordinates (Haversine formula)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Find nearest prognosticator to given coordinates
  const findNearestPrognosticator = (lat, lon) => {
    if (prognosticators.length === 0) return null;

    let nearest = null;
    let minDistance = Infinity;

    for (const prog of prognosticators) {
      const distance = calculateDistance(lat, lon, prog.prognosticator_lat, prog.prognosticator_long);
      if (distance < minDistance) {
        minDistance = distance;
        nearest = { ...prog, distance: Math.round(distance) };
      }
    }

    return nearest;
  };

  // Function to get the next Groundhog Day
  const getNextGroundhogDay = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    let groundhogYear = currentYear;

    if (
      today.getMonth() > 1 ||
      (today.getMonth() === 1 && today.getDate() > 2)
    ) {
      groundhogYear += 1;
    }

    return new Date(groundhogYear, 1, 2);
  };

  // Function to get the previous Groundhog Day
  const getPreviousGroundhogDay = () => {
    const next = getNextGroundhogDay();
    return new Date(next.getFullYear() - 1, 1, 2);
  };

  const nextGroundhogDay = getNextGroundhogDay();
  const previousGroundhogDay = getPreviousGroundhogDay();
  const today = new Date();
  const diffTime = nextGroundhogDay - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  // Check if we can evaluate accuracy (past March of previous prediction year)
  const canEvaluateAccuracy = today > new Date(previousGroundhogDay.getFullYear(), 2, 31);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setCity(value);

    // Clear any pending API search
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Filter cities for autocomplete from static list
    if (value.length >= 2) {
      const filtered = usCities
        .filter(c => c.toLowerCase().startsWith(value.toLowerCase()))
        .slice(0, 8);
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0 || value.length >= 3);

      // If few or no static matches, also search via geocoding API (debounced)
      if (filtered.length < 3 && value.length >= 3) {
        setIsSearchingCities(true);
        searchTimeoutRef.current = setTimeout(async () => {
          try {
            const response = await fetch(
              `https://api.openweathermap.org/geo/1.0/direct?q=${encodeURIComponent(value)},US&limit=5&appid=${weatherApiKey}`
            );
            if (response.ok) {
              const geoResults = await response.json();
              const apiSuggestions = geoResults
                .filter(r => r.country === "US")
                .map(r => `${r.name}, ${r.state || ""}`.trim().replace(/, $/, ""));

              // Merge with static results, avoiding duplicates
              const merged = [...filtered];
              for (const apiCity of apiSuggestions) {
                const normalized = apiCity.toLowerCase();
                if (!merged.some(m => m.toLowerCase() === normalized)) {
                  merged.push(apiCity);
                }
              }
              setSuggestions(merged.slice(0, 8));
              setShowSuggestions(merged.length > 0);
            }
          } catch (err) {
            // Silently fail - static suggestions still shown
          } finally {
            setIsSearchingCities(false);
          }
        }, 300); // 300ms debounce
      }
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setCity(suggestion);
    setSuggestions([]);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const handleInputBlur = () => {
    // Delay to allow click on suggestion
    setTimeout(() => setShowSuggestions(false), 150);
  };

  // Fetch 20 years of Feb/Mar historical data for Bayesian prior
  const fetchHistoricalDataForPrior = async (lat, lon) => {
    const currentYear = new Date().getFullYear();
    const startYear = currentYear - 20;

    // Fetch all years in one request (Open-Meteo supports long date ranges)
    const response = await fetch(
      `https://archive-api.open-meteo.com/v1/archive?latitude=${lat}&longitude=${lon}&start_date=${startYear}-02-01&end_date=${currentYear - 1}-03-31&daily=temperature_2m_max&temperature_unit=fahrenheit&timezone=auto`
    );

    if (!response.ok) {
      if (response.status === 429) {
        throw new Error("Weather service is temporarily busy. Please try again in a few minutes.");
      }
      throw new Error("Failed to fetch historical weather data.");
    }

    const data = await response.json();
    if (!data.daily || !data.daily.temperature_2m_max) {
      throw new Error("No historical data available.");
    }

    // Organize data by year and month
    const yearlyData = {};
    data.daily.time.forEach((date, i) => {
      const temp = data.daily.temperature_2m_max[i];
      if (temp === null) return;

      const year = parseInt(date.substring(0, 4));
      const month = parseInt(date.substring(5, 7));

      if (month !== 2 && month !== 3) return; // Only Feb and Mar

      if (!yearlyData[year]) yearlyData[year] = { feb: [], mar: [] };
      if (month === 2) yearlyData[year].feb.push(temp);
      if (month === 3) yearlyData[year].mar.push(temp);
    });

    // Calculate monthly means for each year
    const yearlyMeans = {};
    for (const year in yearlyData) {
      const febTemps = yearlyData[year].feb;
      const marTemps = yearlyData[year].mar;
      yearlyMeans[year] = {
        feb: febTemps.length > 0 ? febTemps.reduce((a, b) => a + b, 0) / febTemps.length : null,
        mar: marTemps.length > 0 ? marTemps.reduce((a, b) => a + b, 0) / marTemps.length : null
      };
    }

    return yearlyMeans;
  };

  // Calculate 15-year rolling average and classify each year
  const classifyYears = (yearlyMeans) => {
    const years = Object.keys(yearlyMeans).map(Number).sort();
    const classifications = [];

    for (let i = 0; i < years.length; i++) {
      const year = years[i];

      // Need at least 15 prior years for rolling average
      const priorYears = years.filter(y => y < year && y >= year - 15);
      if (priorYears.length < 10) continue; // Need at least 10 years of history

      // Calculate 15-year rolling averages
      const febAvgs = priorYears.map(y => yearlyMeans[y]?.feb).filter(v => v !== null);
      const marAvgs = priorYears.map(y => yearlyMeans[y]?.mar).filter(v => v !== null);

      if (febAvgs.length < 5 || marAvgs.length < 5) continue;

      const febRollingAvg = febAvgs.reduce((a, b) => a + b, 0) / febAvgs.length;
      const marRollingAvg = marAvgs.reduce((a, b) => a + b, 0) / marAvgs.length;

      const currentFeb = yearlyMeans[year]?.feb;
      const currentMar = yearlyMeans[year]?.mar;

      if (currentFeb === null && currentMar === null) continue;

      // Early spring if either month is above its rolling average
      const earlySpring = (currentFeb !== null && currentFeb > febRollingAvg) ||
                          (currentMar !== null && currentMar > marRollingAvg);

      classifications.push({ year, earlySpring });
    }

    return classifications;
  };

  // Calculate the Bayesian probability of early spring
  const calculateProbability = (prior, tempDiff, daysOut) => {
    // Temperature signal: sigmoid function (scales temp difference to 0-1)
    // tempDiff > 0 means forecast is warmer than average
    const tempSignal = 1 / (1 + Math.exp(-tempDiff / 5));

    // Forecast influence: stronger closer to Groundhog Day
    // But prior always maintains at least 40% influence (maxForecastWeight = 0.6)
    const maxForecastWeight = 0.6;
    const forecastConfidence = Math.max(0.2, Math.min(1, (14 - daysOut) / 7));
    const forecastWeight = maxForecastWeight * forecastConfidence;

    // Bayesian-inspired combination: blend forecast signal with prior
    // Prior always has significant weight (40-88%)
    const probability = forecastWeight * tempSignal + (1 - forecastWeight) * prior;

    return {
      probability: Math.round(probability * 100),
      confidence: forecastConfidence > 0.7 ? "high" : forecastConfidence > 0.3 ? "moderate" : "low",
      tempSignal: Math.round(tempSignal * 100),
      prior: Math.round(prior * 100)
    };
  };

  // Fetch recent historical data for temperature comparison
  const fetchRecentHistoricalData = async (lat, lon) => {
    const currentYear = new Date().getFullYear();
    const years = 5;
    const historicalTemps = [];

    for (let i = 1; i <= years; i++) {
      const year = currentYear - i;
      const startDate = `${year}-01-15`;
      const endDate = `${year}-02-15`;

      const response = await fetch(
        `https://archive-api.open-meteo.com/v1/archive?latitude=${lat}&longitude=${lon}&start_date=${startDate}&end_date=${endDate}&daily=temperature_2m_max&temperature_unit=fahrenheit&timezone=auto`
      );

      if (!response.ok) continue;

      const data = await response.json();
      if (data.daily && data.daily.temperature_2m_max) {
        historicalTemps.push(...data.daily.temperature_2m_max.filter(t => t !== null));
      }
    }

    return historicalTemps;
  };

  // Fetch forecast data from Open-Meteo.
  // The forecast endpoint only covers ~16 days ahead, so when Groundhog Day is
  // further out than that we return null and let the caller fall back to the
  // historical climatology baseline (the forecast carries negligible weight
  // that far out anyway).
  const FORECAST_HORIZON_DAYS = 16;

  const fetchForecastData = async (lat, lon, targetDate) => {
    const dateStr = targetDate.toISOString().split("T")[0];

    const msUntilTarget = targetDate - new Date();
    const daysUntilTarget = Math.ceil(msUntilTarget / (1000 * 60 * 60 * 24));
    if (daysUntilTarget > FORECAST_HORIZON_DAYS) {
      // Out of forecast range — caller will use the climatology baseline.
      return null;
    }

    const response = await fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&daily=temperature_2m_max&temperature_unit=fahrenheit&timezone=auto&start_date=${dateStr}&end_date=${dateStr}`
    );

    if (!response.ok) {
      if (response.status === 429) {
        throw new Error("Weather service is temporarily busy. Please try again in a few minutes.");
      }
      throw new Error("Failed to fetch forecast weather data.");
    }

    const data = await response.json();
    return data.daily?.temperature_2m_max?.[0];
  };

  // Fetch historical temperature for a specific date
  const fetchHistoricalTemp = async (lat, lon, date) => {
    const dateStr = date.toISOString().split("T")[0];

    const response = await fetch(
      `https://archive-api.open-meteo.com/v1/archive?latitude=${lat}&longitude=${lon}&start_date=${dateStr}&end_date=${dateStr}&daily=temperature_2m_max&temperature_unit=fahrenheit&timezone=auto`
    );

    if (!response.ok) return null;

    const data = await response.json();
    return data.daily?.temperature_2m_max?.[0];
  };

  // Fetch Feb/Mar data for a specific year to evaluate actual outcome
  const fetchYearData = async (lat, lon, year) => {
    const response = await fetch(
      `https://archive-api.open-meteo.com/v1/archive?latitude=${lat}&longitude=${lon}&start_date=${year}-02-01&end_date=${year}-03-31&daily=temperature_2m_max&temperature_unit=fahrenheit&timezone=auto`
    );

    if (!response.ok) return null;

    const data = await response.json();
    if (!data.daily || !data.daily.temperature_2m_max) return null;

    const febTemps = [];
    const marTemps = [];

    data.daily.time.forEach((date, i) => {
      const temp = data.daily.temperature_2m_max[i];
      if (temp === null) return;
      const month = parseInt(date.substring(5, 7));
      if (month === 2) febTemps.push(temp);
      if (month === 3) marTemps.push(temp);
    });

    return {
      feb: febTemps.length > 0 ? febTemps.reduce((a, b) => a + b, 0) / febTemps.length : null,
      mar: marTemps.length > 0 ? marTemps.reduce((a, b) => a + b, 0) / marTemps.length : null
    };
  };

  // Evaluate if a year actually had early spring using def1 classification
  const evaluateActualOutcome = (yearlyMeans, targetYear) => {
    const years = Object.keys(yearlyMeans).map(Number).sort();

    // Get 15-year rolling average from years before targetYear
    const priorYears = years.filter(y => y < targetYear && y >= targetYear - 15);
    if (priorYears.length < 10) return null;

    const febAvgs = priorYears.map(y => yearlyMeans[y]?.feb).filter(v => v !== null);
    const marAvgs = priorYears.map(y => yearlyMeans[y]?.mar).filter(v => v !== null);

    if (febAvgs.length < 5 || marAvgs.length < 5) return null;

    const febRollingAvg = febAvgs.reduce((a, b) => a + b, 0) / febAvgs.length;
    const marRollingAvg = marAvgs.reduce((a, b) => a + b, 0) / marAvgs.length;

    const targetFeb = yearlyMeans[targetYear]?.feb;
    const targetMar = yearlyMeans[targetYear]?.mar;

    if (targetFeb === null && targetMar === null) return null;

    // Early spring if either month is above its rolling average
    const wasEarlySpring = (targetFeb !== null && targetFeb > febRollingAvg) ||
                           (targetMar !== null && targetMar > marRollingAvg);

    return {
      wasEarlySpring,
      febActual: targetFeb ? Math.round(targetFeb) : null,
      febAvg: Math.round(febRollingAvg),
      marActual: targetMar ? Math.round(targetMar) : null,
      marAvg: Math.round(marRollingAvg)
    };
  };

  // Calculate what the official prediction would have been on a past Feb 2
  const calculatePastPrediction = (yearlyMeans, classifications, historicalAvg, actualTemp, predictionYear) => {
    // Filter classifications to only years before the prediction year
    const priorClassifications = classifications.filter(c => c.year < predictionYear);

    // Calculate prior from years before the prediction
    const earlySpringCount = priorClassifications.filter(c => c.earlySpring).length;
    const prior = priorClassifications.length > 0 ? earlySpringCount / priorClassifications.length : 0.5;

    // Temperature difference on that Feb 2
    const tempDiff = actualTemp - historicalAvg;

    // On Feb 2 itself, daysOut = 0, so forecast confidence is maximum
    const probResult = calculateProbability(prior, tempDiff, 0);

    return {
      ...probResult,
      year: predictionYear,
      actualTemp: Math.round(actualTemp),
      historicalAvg: Math.round(historicalAvg),
      predictedEarlySpring: probResult.probability > 50
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setPrediction(null);
    setNearestPrognosticator(null);
    setProbability(null);
    setPastPrediction(null);
    setPrognosticatorPastPrediction(null);
    setComparisonData(null);
    setIsLoading(true);

    let lat, lon, nearest;

    try {
      if (!city.trim()) {
        throw new Error("Please enter a valid city name.");
      }

      const parts = city.split(",").map((part) => part.trim());
      const cityInput = parts[0];
      const state = parts[1] || "";
      const country = parts[2] || "US";
      const query = state ? `${cityInput},${state},${country}` : `${cityInput},${country}`;

      // Fetch geolocation data
      const geoResponse = await fetch(
        `https://api.openweathermap.org/geo/1.0/direct?q=${query}&limit=1&appid=${weatherApiKey}`
      );

      if (!geoResponse.ok) {
        throw new Error("Failed to fetch location data.");
      }

      const geoData = await geoResponse.json();
      if (!geoData || geoData.length === 0) {
        throw new Error(`Could not find "${city}". Try format: City, State (e.g., Durham, NC)`);
      }

      lat = geoData[0].lat;
      lon = geoData[0].lon;

      // Find nearest prognosticator (no API needed)
      nearest = findNearestPrognosticator(lat, lon);
      setNearestPrognosticator(nearest);

      // Look up historical comparison data for nearest prognosticator (no API needed)
      if (nearest && historicalComparison?.prognosticators) {
        const comparison = historicalComparison.prognosticators[nearest.prognosticator_slug];
        setComparisonData(comparison || null);
      } else {
        setComparisonData(null);
      }

      // Look up prognosticator's prediction for previous year
      if (nearest && predictions.length > 0) {
        const prevYear = previousGroundhogDay.getFullYear();
        const progPred = predictions.find(
          p => p.prognosticator_slug === nearest.prognosticator_slug && p.year === prevYear
        );
        if (progPred && progPred.predict_early_spring !== null) {
          setPrognosticatorPastPrediction({
            prediction: progPred.prediction,
            predictedEarlySpring: progPred.predict_early_spring === 1,
            year: prevYear
          });
        }
      }
    } catch (geoError) {
      // Geocoding failed - can't show anything
      setError(geoError.message || "Failed to find location.");
      setIsLoading(false);
      return;
    }

    // Now try to fetch weather data for prediction (may fail due to rate limits)
    try {
      // Fetch historical data for Bayesian prior (20 years of Feb/Mar)
      const yearlyMeans = await fetchHistoricalDataForPrior(lat, lon);
      const classifications = classifyYears(yearlyMeans);

      // Calculate prior from historical data
      const earlySpringCount = classifications.filter(c => c.earlySpring).length;
      const prior = classifications.length > 0 ? earlySpringCount / classifications.length : 0.5;

      // Fetch recent historical data for temperature comparison
      const historicalTemps = await fetchRecentHistoricalData(lat, lon);

      if (historicalTemps.length === 0) {
        throw new Error("No historical temperature data available.");
      }

      // Calculate historical average temperature
      const avgHistoricalTemp = historicalTemps.reduce((a, b) => a + b, 0) / historicalTemps.length;

      // Fetch forecasted temperature. Null means Groundhog Day is beyond the
      // forecast horizon, so we fall back to the historical climatology
      // baseline (no temperature signal — the prior carries the prediction).
      const forecastTemp = await fetchForecastData(lat, lon, nextGroundhogDay);
      const hasForecast = forecastTemp != null;
      const effectiveTemp = hasForecast ? forecastTemp : avgHistoricalTemp;

      // Calculate temperature difference
      const tempDiff = effectiveTemp - avgHistoricalTemp;

      // Calculate Bayesian probability
      const probResult = calculateProbability(prior, tempDiff, diffDays);
      setProbability(probResult);

      // Calculate past prediction (what we would have said on previous Feb 2)
      const prevYear = previousGroundhogDay.getFullYear();
      const pastFeb2Temp = await fetchHistoricalTemp(lat, lon, previousGroundhogDay);

      if (pastFeb2Temp !== null) {
        // Need historical avg for that time period (years before prevYear)
        const pastHistoricalAvg = avgHistoricalTemp; // Approximation - same baseline
        const pastPred = calculatePastPrediction(yearlyMeans, classifications, pastHistoricalAvg, pastFeb2Temp, prevYear);

        // If we can evaluate accuracy, fetch actual outcome
        if (canEvaluateAccuracy) {
          // We need to include the prediction year's data
          const predYearData = await fetchYearData(lat, lon, prevYear);
          if (predYearData) {
            // Add to yearlyMeans temporarily for evaluation
            const extendedMeans = { ...yearlyMeans, [prevYear]: predYearData };
            const actualOutcome = evaluateActualOutcome(extendedMeans, prevYear);
            if (actualOutcome) {
              pastPred.actualOutcome = actualOutcome;
              pastPred.wasCorrect = pastPred.predictedEarlySpring === actualOutcome.wasEarlySpring;
            }
          }
        }

        setPastPrediction(pastPred);
      }

      // Determine prediction based on probability
      const earlySpring = probResult.probability > 50;

      // Build enriched prompt with probability context
      let progContext = "";
      if (nearest) {
        progContext = `The nearest real-world prognosticator is ${nearest.prognosticator_name}, a ${nearest.prognosticator_creature || nearest.prognosticator_type} located ${nearest.distance} miles away in ${nearest.prognosticator_city}. `;
      }

      const weatherContext = hasForecast
        ? `The forecast high for Groundhog Day is ${Math.round(forecastTemp)}°F, compared to the 5-year historical average of ${Math.round(avgHistoricalTemp)}°F.`
        : `Groundhog Day is still too far out for a weather forecast, so this leans on the historical average high of ${Math.round(avgHistoricalTemp)}°F around that time.`;

      const probabilityContext = `Based on Bayesian analysis: ${probResult.probability}% chance of early spring. Historical prior for this location: ${probResult.prior}% of past years had early springs. Forecast confidence: ${probResult.confidence} (${diffDays} days until Groundhog Day).`;

      const prompt = `There are ${diffDays} days until Groundhog Day on ${nextGroundhogDay.toDateString()}. ${progContext}${weatherContext} ${probabilityContext} Generate a playful prediction message for ${city} that mentions the ${probResult.probability}% probability of ${earlySpring ? "early spring" : "a long winter"}. If forecast confidence is low, playfully note the uncertainty. If a nearby prognosticator was mentioned, include a fun reference to them. Keep the response under 150 tokens.`;

      // Call Vercel serverless function (proxies to OpenAI)
      const apiResponse = await fetch('/api/prediction', {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ prompt }),
      });

      if (!apiResponse.ok) {
        const errorData = await apiResponse.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to fetch prediction.");
      }

      const data = await apiResponse.json();
      setPrediction(data.prediction);
    } catch (weatherError) {
      // Weather API failed, but we can still show the comparison
      setError(weatherError.message || "Unable to generate prediction right now.");
      // Don't return - let the comparison show
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <h1>Hi, I'm Byte Burrower</h1>
      <h2>Your digital groundhog powered by AI and weather data, here to predict whether spring will come early or winter will linger. Enter your city for a fun, personalized forecast tailored just for you!</h2>
      <form onSubmit={handleSubmit}>
        <div className="autocomplete-container">
          <input
            ref={inputRef}
            type="text"
            value={city}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            placeholder="City, State (e.g., Durham, NC)"
            autoComplete="off"
          />
          {showSuggestions && (
            <ul className="suggestions-list">
              {suggestions.map((suggestion, idx) => (
                <li
                  key={idx}
                  onMouseDown={() => handleSuggestionClick(suggestion)}
                >
                  {suggestion}
                </li>
              ))}
              {isSearchingCities && suggestions.length < 3 && (
                <li className="searching-indicator">Searching more cities...</li>
              )}
              {!isSearchingCities && suggestions.length === 0 && city.length >= 3 && (
                <li className="no-results">No matches - try submitting anyway</li>
              )}
            </ul>
          )}
        </div>
        <button type="submit">Get Prediction</button>
        <p className="input-help">Currently optimized for U.S. locations</p>
      </form>
      {isLoading && (
        <div className="loading" aria-live="polite">
          <TailSpin color="#4CAF50" height={50} width={50} />
          <p>Analyzing 20 years of weather data...</p>
        </div>
      )}
      {(prediction || nearestPrognosticator || error) && (
        <div className="results-container">
          {/* Results Header */}
          <div className="results-header">
            <h2>Results for {city}</h2>
            <p className="countdown">
              {diffDays} days until Groundhog Day ({nextGroundhogDay.toDateString()})
            </p>
          </div>

          {/* Error message if weather API failed */}
          {error && <div className="error-message">{error}</div>}

          {/* Section 1: Current Prediction */}
          {(probability || prediction) && (
            <div className="section-card">
              <h3>Prediction for {nextGroundhogDay.getFullYear()}</h3>
              {probability && (
                <div className="probability-display">
                  <div className="probability-bar">
                    <div
                      className="probability-fill"
                      style={{
                        width: `${probability.probability}%`,
                        backgroundColor: probability.probability > 50 ? '#4CAF50' : '#3498DB'
                      }}
                    />
                  </div>
                  <p className="probability-text">
                    <strong>{probability.probability}%</strong> chance of early spring
                    <span className="confidence"> (Forecast confidence: {probability.confidence})</span>
                  </p>
                  <p className="prior-text">
                    <em>Historical baseline: {probability.prior}% of years at this location had early springs</em>
                  </p>
                </div>
              )}
              {prediction && <p className="ai-prediction-message">{prediction}</p>}
            </div>
          )}

          {/* Section 2: Nearest Prognosticator */}
          {nearestPrognosticator && (
            <div className="section-card">
              <h3>Your Nearest Prognosticator</h3>
              <p className="prognosticator-info">
                <a
                  href={`https://countdowntogroundhogday.com/forecaster/${nearestPrognosticator.prognosticator_slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="name"
                >
                  {nearestPrognosticator.prognosticator_name}
                </a>
                {" "}
                <span className="type">({nearestPrognosticator.prognosticator_creature || nearestPrognosticator.prognosticator_type})</span>
                {" — "}
                <span className="location">{nearestPrognosticator.distance} miles away in {nearestPrognosticator.prognosticator_city}</span>
              </p>
              {prognosticatorDescriptions?.descriptions?.[nearestPrognosticator.prognosticator_slug] && (
                <p className="prognosticator-description">
                  {prognosticatorDescriptions.descriptions[nearestPrognosticator.prognosticator_slug]}
                </p>
              )}
            </div>
          )}

          {/* Section 3: Historical Accuracy Comparison */}
          {comparisonData && comparisonData.total_years >= 5 && nearestPrognosticator && (
            <div className="section-card blue-accent accuracy-comparison">
              <h3>Historical Accuracy Comparison</h3>

              <div className="accuracy-summary">
                <div className="accuracy-stat">
                  <div className="label">Byte Burrower</div>
                  <div className="value green">{comparisonData.byteburrower_accuracy}%</div>
                </div>
                <div className="accuracy-stat">
                  <div className="label">{nearestPrognosticator.prognosticator_name}</div>
                  <div className="value blue">{comparisonData.prognosticator_accuracy}%</div>
                </div>
              </div>

              <p className="comparison-explainer">
                Byte Burrower's historical predictions are calculated retroactively using the same algorithm on past weather data.
              </p>

              <div className="comparison-table-container">
                <table className="comparison-table">
                  <thead>
                    <tr>
                      <th>Year</th>
                      <th>Byte Burrower</th>
                      <th>{nearestPrognosticator.prognosticator_name}</th>
                      <th>Actual</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...comparisonData.yearly_data].reverse().map((year) => (
                      <tr key={year.year}>
                        <td className="year-cell">{year.year}</td>
                        <td className={year.byteburrower_correct ? "correct-cell" : "incorrect-cell"}>
                          {year.byteburrower_prediction ? "Early Spring" : "Long Winter"}
                          <span className="result-icon">{year.byteburrower_correct ? " ✓" : " ✗"}</span>
                        </td>
                        <td className={year.prognosticator_correct ? "correct-cell" : "incorrect-cell"}>
                          {year.prognosticator_prediction ? "Early Spring" : "Long Winter"}
                          <span className="result-icon">{year.prognosticator_correct ? " ✓" : " ✗"}</span>
                        </td>
                        <td className="actual-cell">
                          {year.actual_outcome ? "Early Spring" : "Long Winter"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="table-note">{comparisonData.total_years} years compared (earlier years lack sufficient historical data for Byte Burrower's rolling average)</p>
            </div>
          )}

          {/* Section 4: Past Year's Predictions Comparison */}
          {pastPrediction && (
            <div className="section-card blue-accent past-prediction">
              <h3>{pastPrediction.year} Prediction Comparison</h3>

              <div className="past-comparison-grid">
                {/* Byte Burrower's prediction */}
                <div className="past-comparison-item">
                  <div className="predictor-name">Byte Burrower</div>
                  <div className="predictor-prediction">
                    {pastPrediction.predictedEarlySpring ? "Early Spring" : "Long Winter"}
                    <span className="probability-badge">{pastPrediction.probability}%</span>
                  </div>
                  {pastPrediction.actualOutcome && (
                    <div className={`predictor-result ${pastPrediction.wasCorrect ? 'correct' : 'incorrect'}`}>
                      {pastPrediction.wasCorrect ? "✓ Correct" : "✗ Incorrect"}
                    </div>
                  )}
                </div>

                {/* Prognosticator's prediction */}
                {prognosticatorPastPrediction && nearestPrognosticator && (
                  <div className="past-comparison-item">
                    <div className="predictor-name">{nearestPrognosticator.prognosticator_name}</div>
                    <div className="predictor-prediction">
                      {prognosticatorPastPrediction.predictedEarlySpring ? "Early Spring" : "Long Winter"}
                    </div>
                    {pastPrediction.actualOutcome && (
                      <div className={`predictor-result ${prognosticatorPastPrediction.predictedEarlySpring === pastPrediction.actualOutcome.wasEarlySpring ? 'correct' : 'incorrect'}`}>
                        {prognosticatorPastPrediction.predictedEarlySpring === pastPrediction.actualOutcome.wasEarlySpring ? "✓ Correct" : "✗ Incorrect"}
                      </div>
                    )}
                  </div>
                )}

                {/* Show message if prognosticator didn't make a prediction */}
                {!prognosticatorPastPrediction && nearestPrognosticator && (
                  <div className="past-comparison-item">
                    <div className="predictor-name">{nearestPrognosticator.prognosticator_name}</div>
                    <div className="predictor-prediction no-prediction">
                      No prediction recorded
                    </div>
                  </div>
                )}
              </div>

              {pastPrediction.actualOutcome && (
                <div className="actual-outcome-box">
                  <p>
                    <strong>Actual Result:</strong>{" "}
                    {pastPrediction.actualOutcome.wasEarlySpring ? "Early Spring" : "Long Winter"}
                  </p>
                  <p className="accuracy-details">
                    Feb avg: {pastPrediction.actualOutcome.febActual}°F (15-yr avg: {pastPrediction.actualOutcome.febAvg}°F)
                    {" | "}
                    Mar avg: {pastPrediction.actualOutcome.marActual}°F (15-yr avg: {pastPrediction.actualOutcome.marAvg}°F)
                  </p>
                </div>
              )}

              {!pastPrediction.actualOutcome && canEvaluateAccuracy && (
                <p className="accuracy-pending">
                  <em>Accuracy evaluation data not available for this location.</em>
                </p>
              )}
              {!canEvaluateAccuracy && (
                <p className="accuracy-pending">
                  <em>Check back after March {pastPrediction.year} to see if these predictions were correct!</em>
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Home;
